/**
 * 推送记录页
 */
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Form, Row, Col, Button, Modal, Input, message, Cascader, DatePicker, Select } from 'antd';
import { useTable } from '@/utils/useTable';
import { formSearchGutter } from '@/constants/common';
import { getAlarmRecords, updateAlarmRecord } from '@/service/jtAlarmConfig';
import { Alarm808PushRecord } from '@/service/jtAlarmConfig/define';
import { jtDeviceAlarmTypeData } from '@/constants/jtDeviceAlarm';
import {
  getAlarmTypeCodeFromValue,
  displayJtDeviceAlarmV2
} from '@/utils/jtDeviceAlarm';
import CommonTable from '@/components/commonTable';
import RenderEllipsisColumn from '@/components/renderEllipsisColumn';
import CarTree from '@/components/carTree';
import VideoPreviewModal from '@/components/videoPreviewModal';
import ImagePreviewModal from '@/components/imagePreviewModal';
import BatchOperationModal from './components/BatchOperationModal';
import Thumbnail from '@/components/thumbnail';
import { jtDeviceAlarmVideoRequestV2 } from '@/service/jtDeviceAlarm';
import { changeUrlProtocol } from '@/utils/common';
import { eventEmitter, EVENTS } from '@/utils/eventEmitter';
import styles from '@/styles/pageContent.module.scss';
import dayjs from 'dayjs';

const { TextArea } = Input;
const { RangePicker } = DatePicker;

export const JtAlarmRecord: React.FC = () => {
  const [form] = Form.useForm();
  const [remarkForm] = Form.useForm();
  const carTreeRef = useRef<any>(null); // CarTree组件引用
  const [searchParams, setSearchParams] = useState<any>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [batchModalVisible, setBatchModalVisible] = useState(false);
  const [currentRecord, setCurrentRecord] = useState<Alarm808PushRecord | null>(null);
  const [selectedRecords, setSelectedRecords] = useState<Alarm808PushRecord[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false); // 是否搜索过
  const [isFirstLoad, setIsFirstLoad] = useState(true); // 首次加载标识
  const [attachmentInfo, setAttachmentInfo] = useState<any>({}); // 附件展示的信息

  // 生成默认全选的报警类型
  const defaultAlarmTypes = useMemo(() => {
    return jtDeviceAlarmTypeData
      .map((item: any) =>
        item.children.map((item1: any) => [item.value, item1.value])
      )
      .flat(1);
  }, []);

  // 初始化时设置默认报警类型
  useEffect(() => {
    form.setFieldsValue({
      alarmTypes: defaultAlarmTypes
    });
  }, [defaultAlarmTypes, form]);

  // 首次进入时自动选择所有车并查询
  useEffect(() => {
    if (isFirstLoad && carTreeRef.current) {
      const timer = setTimeout(() => {
        const { carIds } = carTreeRef.current.getCheckedCarInfos();
        if (carIds && carIds.length > 0) {
          form.submit();
          setIsFirstLoad(false);
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isFirstLoad, form]);

  const { page, limit, loading, data, totalCount, onPageChange, getData } =
    useTable({
      requestFunc: getAlarmRecords,
      initRequest: false,
      searchParams
    });

  // 搜索
  const onSearch = async (values: any) => {
    const { alarmTypes, timeRange, remarkFlag } = values;

    // 从CarTree组件获取当前显示且选中的车辆
    const { carIds } = carTreeRef.current?.getCheckedCarInfos() || { carIds: [] };

    // 检查是否选中车辆
    if (carIds.length === 0) {
      message.warning('请选择车辆');
      return;
    }

    // 处理报警类型参数
    const alarmTypeList = alarmTypes.map((item: string[]) => {
      const [alarmType, warningType] = getAlarmTypeCodeFromValue(item);
      return { alarmType, warningType };
    });

    const params: any = {
      alarmTypeList,
      carIdList: carIds,
      remarkFlag
    };

    // 如果选择了时间范围,才加入参数
    if (timeRange) {
      params.start = dayjs(timeRange[0]).format('YYYY-MM-DD HH:mm:ss');
      params.end = dayjs(timeRange[1]).format('YYYY-MM-DD HH:mm:ss');
    }

    setSearchParams(params);
    setHasSearched(true);
    // 清空选中的记录
    setSelectedRecords([]);
    setSelectedRowKeys([]);

    // 触发刷新报警数量
    eventEmitter.emit(EVENTS.REFRESH_ALARM_COUNT);
  };

  // 打开处理弹窗
  const handleOpenModal = (record: Alarm808PushRecord) => {
    setCurrentRecord(record);
    remarkForm.setFieldsValue({ remark: record.remark || '' });
    setModalVisible(true);
  };

  // 关闭弹窗
  const handleCloseModal = () => {
    setModalVisible(false);
    setCurrentRecord(null);
    remarkForm.resetFields();
  };

  // 提交处理意见
  const handleSubmit = async () => {
    if (!currentRecord?.id) return;
    try {
      setSubmitLoading(true);
      const values = await remarkForm.validateFields();
      const res = await updateAlarmRecord({
        id: currentRecord.id,
        remark: values.remark
      } as any);
      if (res?.data?.success) {
        message.success('处理成功');
        handleCloseModal();
        getData();
        // 触发刷新报警数量
        eventEmitter.emit(EVENTS.REFRESH_ALARM_COUNT);
      } else {
        message.error(res?.data?.msg || '处理失败');
      }
    } catch (error) {
      console.error('处理失败:', error);
      message.error('处理失败，请稍后重试');
    } finally {
      setSubmitLoading(false);
    }
  };

  // 打开批量处理弹窗
  const handleOpenBatchModal = () => {
    if (selectedRecords.length === 0) {
      message.warning('请先选择要处理的记录');
      return;
    }
    setBatchModalVisible(true);
  };

  // 关闭批量处理弹窗
  const handleCloseBatchModal = () => {
    setBatchModalVisible(false);
  };

  // 批量处理成功回调
  const handleBatchSuccess = () => {
    setSelectedRecords([]);
    setSelectedRowKeys([]);
    getData();
    // 触发刷新报警数量
    eventEmitter.emit(EVENTS.REFRESH_ALARM_COUNT);
  };

  // 查看报警附件
  const handleViewAttachment = async (record: Alarm808PushRecord) => {
    const osses = (record as any).osses;
    if (!osses || osses.length === 0) return;

    if (osses.length > 1) {
      // 有多个附件时显示缩略图
      const allAttachmentRequest = osses.map((oss: { ossKey: string, ossGroup: string, ossBucket: string }) =>
        jtDeviceAlarmVideoRequestV2(oss.ossKey, oss.ossGroup, oss.ossBucket)
      );
      const attachmentData: any = [];
      const res = await Promise.allSettled(allAttachmentRequest);
      res.forEach((item: any, index: number) => {
        attachmentData.push({
          url: changeUrlProtocol(item.value?.data?.data),
          contentType: osses[index].ossKey.endsWith('mp4') ? 'video' : 'image'
        });
      });
      setAttachmentInfo({ type: 'attachment', attachmentData });
    } else if (osses.length === 1) {
      // 只有一条数据时直接显示
      const res = await jtDeviceAlarmVideoRequestV2(osses[0].ossKey, osses[0].ossGroup, osses[0].ossBucket);
      setAttachmentInfo({
        type: osses[0]?.ossKey.endsWith('mp4') ? 'video' : 'image',
        url: changeUrlProtocol(res.data.data)
      });
    }
  };

  // 表格列定义
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 80,
      ellipsis: { showTitle: false },
      render: (value: number) => <RenderEllipsisColumn text={value} />
    },
    {
      title: '车牌号',
      dataIndex: 'plateNumber',
      width: 120,
      ellipsis: { showTitle: false },
      render: (value: string) => <RenderEllipsisColumn text={value} />
    },
    {
      title: '报警类型',
      dataIndex: 'alarmLabel',
      width: 160,
      ellipsis: { showTitle: false },
      render: (_: unknown, record: Alarm808PushRecord) => {
        const label = displayJtDeviceAlarmV2({
          alarmType: Number(record.alarmType ?? 0),
          warningType: Number(record.warningType ?? 0)
        });
        return <RenderEllipsisColumn text={label || '-'} />;
      }
    },
    {
      title: 'GPS速度',
      dataIndex: 'speed',
      width: 100,
      ellipsis: { showTitle: false },
      render: (value: number) => <RenderEllipsisColumn text={value} />
    },
    {
      title: '车身车速',
      dataIndex: 'vehicleSpeed',
      width: 100,
      ellipsis: { showTitle: false },
      render: (value: number) => <RenderEllipsisColumn text={value} />
    },
    {
      title: '上报时间',
      dataIndex: 'reportTime',
      width: 180,
      ellipsis: { showTitle: false },
      render: (value: string) => <RenderEllipsisColumn text={value} />
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      width: 180,
      ellipsis: { showTitle: false },
      render: (value: string) => <RenderEllipsisColumn text={value} />
    },
    {
      title: '处理意见',
      dataIndex: 'remark',
      width: 150,
      ellipsis: { showTitle: false },
      render: (value: string) => <RenderEllipsisColumn text={value} />
    },
    {
      title: '操作',
      dataIndex: 'operate',
      width: 150,
      fixed: 'right' as const,
      render: (_: any, record: Alarm808PushRecord) => {
        const osses = (record as any).osses;
        const hasAttachment = osses && osses.length > 0;
        return (
          <div className={styles.tableOperateColumn}>
            {!(record?.remark?.trim()) && (
              <Button type="link" onClick={() => handleOpenModal(record)}>
                处理
              </Button>
            )}
            {hasAttachment && (
              <Button type="link" onClick={() => handleViewAttachment(record)}>
                查看报警附件
              </Button>
            )}
          </div>
        );
      }
    }
  ];

  return (
    <div className={styles.spaceBetweenPageContentBox}>
      <div className={styles.carTree}>
        <CarTree
          ref={carTreeRef}
          checkable
          autoCheckAll
          showCheckActions
        />
      </div>
      <div className={styles.contentBox}>
        <Form onFinish={onSearch} form={form} autoComplete="off">
          <Row gutter={formSearchGutter}>
            <Col span={8}>
              <Form.Item
                name="alarmTypes"
                label="报警类型"
                rules={[{ required: true, message: '请选择报警类型' }]}
              >
                <Cascader
                  multiple
                  options={jtDeviceAlarmTypeData}
                  maxTagCount="responsive"
                  allowClear
                  showCheckedStrategy={Cascader.SHOW_CHILD}
                  showSearch
                  placeholder="请选择报警类型"
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="timeRange"
                label="时间范围"
              >
                <RangePicker
                  showTime
                  format="YYYY-MM-DD HH:mm:ss"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                name="remarkFlag"
                label="是否已处理"
                initialValue={false}
              >
                <Select
                  options={[
                    { label: '未处理', value: false },
                    { label: '已处理', value: true }
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={3}>
              <Form.Item>
                <div className={styles.searchBox}>
                  <Button type="primary" htmlType="submit">
                    搜索
                  </Button>
                  <Button
                    type="primary"
                    onClick={handleOpenBatchModal}
                    disabled={selectedRecords.length === 0}
                    style={{ marginLeft: 8 }}
                  >
                    批量操作
                  </Button>
                </div>
              </Form.Item>
            </Col>
          </Row>
        </Form>
        {hasSearched ? (
          <div className={styles.tableBox}>
            <CommonTable
              rowKey="id"
              columns={columns}
              dataSource={data}
              pagination={{
                total: totalCount,
                current: page,
                pageSize: limit,
                onChange: onPageChange
              }}
              rowSelection={{
                selectedRowKeys,
                onChange: (selectedKeys: React.Key[], selectedRows: Alarm808PushRecord[]) => {
                  setSelectedRowKeys(selectedKeys);
                  setSelectedRecords(selectedRows);
                }
              }}
              bordered
              loading={loading}
            />
          </div>
        ) : (
          <div className={styles.tipBox}>请在左侧选择车辆后点击"搜索"按钮</div>
        )}
      </div>
      <Modal
        title="处理"
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={handleCloseModal}
        confirmLoading={submitLoading}
        destroyOnClose
      >
        <Form form={remarkForm} layout="vertical">
          <Form.Item label="处理意见" name="remark">
            <TextArea rows={4} placeholder="请输入处理意见" />
          </Form.Item>
        </Form>
      </Modal>
      {/* 批量处理弹窗 */}
      <BatchOperationModal
        visible={batchModalVisible}
        selectedRecords={selectedRecords}
        onClose={handleCloseBatchModal}
        onSuccess={handleBatchSuccess}
      />
      {/* 预览视频的弹窗 */}
      {attachmentInfo?.type === 'video' && (
        <VideoPreviewModal
          url={attachmentInfo.url}
          fileName="报警视频"
          closeModal={() => setAttachmentInfo({})}
          showDownload={false}
        />
      )}
      {/* 预览照片的弹窗 */}
      {attachmentInfo?.type === 'image' && (
        <ImagePreviewModal
          url={attachmentInfo.url}
          fileName="报警图片"
          closeModal={() => setAttachmentInfo({})}
          showDownload={false}
        />
      )}
      {/* 预览多个附件的弹窗 */}
      {attachmentInfo?.type === 'attachment' && (
        <Modal
          title="报警附件"
          open
          footer={null}
          onCancel={() => setAttachmentInfo({})}
          centered
        >
          <Thumbnail
            dataSource={attachmentInfo.attachmentData || []}
            downloadImgName="报警图片"
            downloadVideoName="报警视频"
            size="large"
            showDownload={false}
          />
        </Modal>
      )}
    </div>
  );
};

export default JtAlarmRecord;